import React from "react";
import { Form, Formik, FormikProps } from "formik";
import { Button, FormControl, InputLabel, Select, MenuItem, TextField, Box, Grid } from "@mui/material";
import { styled } from '@mui/material/styles';
import ManagersSelectInput from "../classifier/ManagersSelectInput";
import EmailFilterModel from "../../core/api/email/EmailFilterModel";
import { DatePicker } from "../common/DatePicker";
import { translations } from "../../translations/common";

const RootForm = styled(Form)({
  width: '100%',
});

const MarginContainer = styled(Grid)(({ theme }) => ({
  margin: theme.spacing(1),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  margin: theme.spacing(1),
  width: '100%',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(1),
  width: '100%',
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  margin: theme.spacing(1),
  width: '100%',
}));

const DatePickerContainer = styled(Box)(({ theme }) => ({
  margin: theme.spacing(1),
  width: '100%',
}));

const StyledManagersSelect = styled(ManagersSelectInput)(({ theme }) => ({
  margin: theme.spacing(1),
  width: '100%',
}));

const InputsContainer = styled(Box)({
  width: '100%',
  '& .MuiInputBase-root': { width: '100%' },
  '& .MuiOutlinedInput-root': { width: '100%' },
  '& .MuiFormControl-root': { width: '100%' }
});

interface Props {
  filter: EmailFilterModel;
  onFilterChange: (filter: EmailFilterModel) => void;
}

const EmailFilterForm: React.FC<Props> = (props) => {
  const onClear = () => {
    props.onFilterChange({
      dateFrom: "",
      dateTo: "",
      user: "",
      status: "",
      email: "",
      clientId: "",
      opened: "",
    });
  };

  return (
    <Formik
      initialValues={props.filter}
      enableReinitialize
      onSubmit={(values, actions) => {
        props.onFilterChange(values);
        actions.setSubmitting(false);
      }}
    >
      {(props: FormikProps<EmailFilterModel>) => {
        const { values, handleChange, isSubmitting, errors } = props;
        return (
          <RootForm>
            <InputsContainer>
              <MarginContainer
                container
                direction="column"
                justifyContent="center"
                alignItems="stretch"
                spacing={1}
              >
                <Grid size={12}>
                  <StyledTextField
                    fullWidth
                    id="email"
                    type="text"
                    name="email"
                    label="El. paštas"
                    onChange={handleChange}
                    value={values.email}
                  />
                </Grid>
                <Grid size={12}>
                  <StyledTextField
                    id="clientId"
                    fullWidth
                    type="number"
                    name="clientId"
                    label="Kliento ID"
                    onChange={handleChange}
                    value={values.clientId}
                  />
                </Grid>
                <Grid size={12}>
                  <StyledFormControl fullWidth>
                    <InputLabel id="email-status-label">
                      Statusas
                    </InputLabel>
                    <Select
                      name="status"
                      labelId="email-status-label"
                      id="status"
                      value={values.status}
                      label="Statusas"
                      fullWidth
                      onChange={handleChange}
                    >
                      <MenuItem value="">Nepasirinktas</MenuItem>
                      <MenuItem value="ERROR">Klaida</MenuItem>
                      <MenuItem value="SENT">Išsiųsta</MenuItem>
                    </Select>
                  </StyledFormControl>
                </Grid>
                <Grid size={12}>
                  <StyledFormControl fullWidth>
                    <InputLabel id="email-opened-label">
                      Peržiūrėtas
                    </InputLabel>
                    <Select
                      name="opened"
                      labelId="email-opened-label"
                      id="opened"
                      value={values.opened}
                      label="Peržiūrėtas"
                      fullWidth
                      onChange={handleChange}
                    >
                      <MenuItem value="">Visi laiškai</MenuItem>
                      <MenuItem value="true">Taip</MenuItem>
                      <MenuItem value="false">Ne</MenuItem>
                    </Select>
                  </StyledFormControl>
                </Grid>
                <Grid size={12}>
                  <DatePickerContainer>
                    <DatePicker
                      id="dateFrom"
                      name="dateFrom"
                      label={translations.datePickerLabels.periodFrom || "Nuo"}
                      value={values.dateFrom}
                      onChange={handleChange}
                      error={!!errors.dateFrom}
                      helperText={errors.dateFrom}
                      fullWidth
                    />
                  </DatePickerContainer>
                </Grid>
                <Grid size={12}>
                  <DatePickerContainer>
                    <DatePicker
                      id="dateTo"
                      name="dateTo"
                      label={translations.datePickerLabels.periodTo || "Iki"}
                      value={values.dateTo}
                      onChange={handleChange}
                      error={!!errors.dateTo}
                      helperText={errors.dateTo}
                      fullWidth
                    />
                  </DatePickerContainer>
                </Grid>
                <Grid size={12}>
                  <StyledManagersSelect
                    name="user"
                    labelId="user-responsible-label"
                    id="user"
                    value={values.user}
                    label="Išsiuntęs asmuo"
                    fullWidth
                    onChange={handleChange}
                  />
                </Grid>
                <Grid size={12}>
                  <StyledButton
                    type="submit"
                    disabled={isSubmitting}
                    variant="contained"
                    color="primary"
                    fullWidth
                  >
                    Filtruoti
                  </StyledButton>
                  <StyledButton
                    type="button"
                    variant="contained"
                    color="secondary"
                    onClick={onClear}
                    fullWidth
                  >
                    Išvalyti
                  </StyledButton>
                </Grid>
              </MarginContainer>
            </InputsContainer>
          </RootForm>
        );
      }}
    </Formik>
  );
};

export default EmailFilterForm;
